import React from 'react';

import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';

import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Checkbox } from '../../components/ui/checkbox';
import { Switch } from '../../components/ui/switch';
import { Plus, Edit2, Save, Search, Calculator, Building } from 'lucide-react';
import { ACTIVITY_STATUS, STATUS_BADGE_COLORS } from './nomenclatureConstants';

function getStatusBadge(status) {
  const cfg = ACTIVITY_STATUS.find((s) => s.value === status) || ACTIVITY_STATUS[0];
  const Icon = cfg.icon;
  const cls = STATUS_BADGE_COLORS[cfg.color] || STATUS_BADGE_COLORS.gray;
  return (<Badge className={cls}><Icon className="h-3 w-3 mr-1" />{cfg.label}</Badge>);
}

export default function NomenclatureFormDialog({ ctx }) {
  const { categories, dispositifs, estimateurs, dispositifSearch, estimateurSearch, createDialogOpen, setCreateDialogOpen, editDialogOpen, setEditDialogOpen, formData, setFormData, handleCreate, handleUpdate, resetForm } = ctx;
  return (
    <>
      {/* Dialog Création/Édition */}
      <Dialog open={createDialogOpen || editDialogOpen} onOpenChange={(open) => { 
        if (!open) { setCreateDialogOpen(false); setEditDialogOpen(false); resetForm(); }
      }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {editDialogOpen ? <Edit2 className="h-5 w-5" /> : <Plus className="h-5 w-5" />}
              {editDialogOpen ? 'Modifier la nomenclature' : 'Nouvelle nomenclature'}
            </DialogTitle>
            <DialogDescription>
              {editDialogOpen ? 'Modifiez les informations de cette activité' : 'Créez une nouvelle activité dans la nomenclature'}
            </DialogDescription>
          </DialogHeader>
          
          <div className="grid gap-6 py-4">
            {/* Informations de base */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Code *</Label>
                <Input 
                  value={formData.code} 
                  onChange={(e) => setFormData({...formData, code: e.target.value})}
                  placeholder="Ex: GO-01"
                />
              </div>
              <div className="space-y-2">
                <Label>Catégorie *</Label>
                <Select value={formData.category} onValueChange={(val) => setFormData({...formData, category: val})}>
                  <SelectTrigger>
                    <SelectValue placeholder="Sélectionner catégorie" />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map(cat => (
                      <SelectItem key={cat.id || cat.name} value={cat.name}>{cat.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Nom de l'activité *</Label>
              <Input 
                value={formData.name} 
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                placeholder="Nom de l'activité"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Définition</Label>
              <Textarea 
                value={formData.definition} 
                onChange={(e) => setFormData({...formData, definition: e.target.value})}
                placeholder="Description détaillée de l'activité..."
                rows={4}
              />
            </div>
            
            <div className="space-y-2">
              <Label>Exclusions</Label>
              <Textarea 
                value={formData.exclusions} 
                onChange={(e) => setFormData({...formData, exclusions: e.target.value})}
                placeholder="Ce qui n'est pas couvert..."
                rows={3}
              />
            </div>
            
            {/* Statut et options */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Statut</Label>
                <Select value={formData.status} onValueChange={(val) => setFormData({...formData, status: val})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ACTIVITY_STATUS.filter(s => s.value !== 'archived').map(s => (
                      <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-4 p-4 bg-blue-50 rounded-lg">
                <div className="flex items-center justify-between">
                  <Label className="text-blue-800">Applicable "Devenir Membre"</Label>
                  <Switch 
                    checked={formData.applicable_devenir_membre}
                    onCheckedChange={(val) => setFormData({...formData, applicable_devenir_membre: val})}
                  />
                </div>
                {formData.applicable_devenir_membre && (
                  <Select 
                    value={formData.devenir_membre_status} 
                    onValueChange={(val) => setFormData({...formData, devenir_membre_status: val})}
                  >
                    <SelectTrigger className="bg-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="active">Activé</SelectItem>
                      <SelectItem value="pending">En attente</SelectItem>
                      <SelectItem value="inactive">Désactivé</SelectItem>
                    </SelectContent>
                  </Select>
                )}
              </div>
            </div>
            
            {/* Dispositifs (V36.16 — listing amélioré) */}
            {(() => {
              const selectedSet = new Set(formData.dispositifs || []);
              // Filtrer + trier alphabétique (le "Test Dispositif" reste visible mais en fin de liste)
              const visibleDispositifs = (dispositifs || [])
                .filter((d) => {
                  const q = (dispositifSearch || '').trim().toLowerCase();
                  if (!q) return true;
                  return ((d.nom || d.name || '') + ' ' + (d.code || '') + ' ' + (d.description || ''))
                    .toLowerCase().includes(q);
                })
                .slice()
                .sort((a, b) => {
                  const aTest = /test/i.test(a.nom || a.name || '');
                  const bTest = /test/i.test(b.nom || b.name || '');
                  if (aTest !== bTest) return aTest ? 1 : -1;
                  // Sélectionnés en premier puis alphabétique
                  const aSel = selectedSet.has(a.id);
                  const bSel = selectedSet.has(b.id);
                  if (aSel !== bSel) return aSel ? -1 : 1;
                  return (a.nom || a.name || '').localeCompare(b.nom || b.name || '', 'fr');
                });
              const allVisibleSelected =
                visibleDispositifs.length > 0
                && visibleDispositifs.every((d) => selectedSet.has(d.id));
              return (
                <div className="space-y-3 p-4 border rounded-lg bg-gradient-to-br from-slate-50/60 to-indigo-50/30">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                    <Label className="text-base font-semibold flex items-center gap-2 text-indigo-900">
                      <Building className="h-4 w-4" />
                      Dispositifs applicables
                      <span className="ml-1 inline-flex items-center justify-center text-[11px] font-bold bg-indigo-100 text-indigo-700 rounded-full px-2 py-0.5">
                        {selectedSet.size}/{dispositifs.length} sélectionné{selectedSet.size > 1 ? 's' : ''}
                      </span>
                    </Label>
                    <div className="flex items-center gap-2">
                      <div className="relative">
                        <Search className="h-3.5 w-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
                        <input
                          type="text"
                          value={dispositifSearch}
                          onChange={(e) => setDispositifSearch(e.target.value)}
                          placeholder="Rechercher…"
                          className="text-xs pl-7 pr-2 py-1.5 border border-slate-200 rounded-md w-44 focus:outline-none focus:ring-1 focus:ring-indigo-300"
                          data-testid="dispositifs-search"
                        />
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          if (allVisibleSelected) {
                            // Tout désélectionner (uniquement les visibles)
                            const ids = new Set(visibleDispositifs.map((d) => d.id));
                            setFormData({
                              ...formData,
                              dispositifs: (formData.dispositifs || []).filter((id) => !ids.has(id)),
                            });
                          } else {
                            // Tout sélectionner (en complément)
                            const next = new Set(formData.dispositifs || []);
                            visibleDispositifs.forEach((d) => next.add(d.id));
                            setFormData({ ...formData, dispositifs: [...next] });
                          }
                        }}
                        className="text-[11px] font-semibold px-2.5 py-1.5 rounded-md border border-indigo-200 bg-white text-indigo-700 hover:bg-indigo-50"
                        data-testid="dispositifs-toggle-all"
                      >
                        {allVisibleSelected ? 'Tout désélectionner' : 'Tout sélectionner'}
                      </button>
                    </div>
                  </div>

                  {visibleDispositifs.length === 0 ? (
                    <p className="text-sm text-slate-500 italic py-4 text-center">
                      {dispositifs.length === 0
                        ? 'Aucun dispositif disponible'
                        : 'Aucun dispositif ne correspond à votre recherche'}
                    </p>
                  ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-72 overflow-y-auto pr-1">
                      {visibleDispositifs.map((d) => {
                        const checked = selectedSet.has(d.id);
                        const isTest = /test/i.test(d.nom || d.name || '');
                        const toggleDispositif = () => {
                          if (checked) {
                            setFormData({
                              ...formData,
                              dispositifs: (formData.dispositifs || []).filter((id) => id !== d.id),
                            });
                          } else {
                            setFormData({
                              ...formData,
                              dispositifs: [...(formData.dispositifs || []), d.id],
                            });
                          }
                        };
                        return (
                          <button
                            type="button"
                            key={d.id}
                            onClick={toggleDispositif}
                            className={`text-left flex items-start gap-2.5 px-3 py-2 rounded-md border transition-all ${
                              checked
                                ? 'bg-indigo-50 border-indigo-300 ring-1 ring-indigo-200'
                                : 'bg-white border-slate-200 hover:border-indigo-200 hover:bg-slate-50'
                            } ${isTest ? 'opacity-60' : ''}`}
                            data-testid={`dispositif-card-${d.id}`}
                          >
                            <Checkbox
                              checked={checked}
                              onCheckedChange={toggleDispositif}
                              onClick={(e) => e.stopPropagation()}
                              className="mt-0.5 flex-shrink-0"
                            />
                            <div className="min-w-0 flex-1">
                              <div className={`text-sm font-medium leading-snug break-words ${checked ? 'text-indigo-900' : 'text-slate-800'}`}>
                                {d.nom || d.name}
                                {isTest && (
                                  <span className="ml-1.5 text-[9px] font-bold text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded">
                                    TEST
                                  </span>
                                )}
                              </div>
                              {(d.code || d.description) && (
                                <div className="text-[11px] text-slate-500 leading-snug mt-0.5">
                                  {d.code && <span className="font-mono mr-1.5">{d.code}</span>}
                                  {d.description && (
                                    <span className="line-clamp-2">{d.description}</span>
                                  )}
                                </div>
                              )}
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })()}
            
            {/* Estimateurs (V36.17 — listing amélioré, aligné sur Dispositifs) */}
            {(() => {
              const selectedSet = new Set(formData.estimateurs || []);
              const visibleEstimateurs = (estimateurs || [])
                .filter((e) => {
                  const q = (estimateurSearch || '').trim().toLowerCase();
                  if (!q) return true;
                  return ((e.name || e.nom || '') + ' ' + (e.code || '') + ' ' + (e.description || ''))
                    .toLowerCase().includes(q);
                })
                .slice()
                .sort((a, b) => {
                  const aTest = /test/i.test(a.name || a.nom || '');
                  const bTest = /test/i.test(b.name || b.nom || '');
                  if (aTest !== bTest) return aTest ? 1 : -1;
                  const aSel = selectedSet.has(a.id);
                  const bSel = selectedSet.has(b.id);
                  if (aSel !== bSel) return aSel ? -1 : 1;
                  return (a.name || a.nom || '').localeCompare(b.name || b.nom || '', 'fr');
                });
              const allVisibleSelected =
                visibleEstimateurs.length > 0
                && visibleEstimateurs.every((e) => selectedSet.has(e.id));
              return (
                <div className="space-y-3 p-4 border rounded-lg bg-gradient-to-br from-slate-50/60 to-emerald-50/30">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                    <Label className="text-base font-semibold flex items-center gap-2 text-emerald-900">
                      <Calculator className="h-4 w-4" />
                      Estimateurs liés
                      <span className="ml-1 inline-flex items-center justify-center text-[11px] font-bold bg-emerald-100 text-emerald-700 rounded-full px-2 py-0.5">
                        {selectedSet.size}/{estimateurs.length} sélectionné{selectedSet.size > 1 ? 's' : ''}
                      </span>
                    </Label>
                    <div className="flex items-center gap-2">
                      <div className="relative">
                        <Search className="h-3.5 w-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
                        <input
                          type="text"
                          value={estimateurSearch}
                          onChange={(e) => setEstimateurSearch(e.target.value)}
                          placeholder="Rechercher…"
                          className="text-xs pl-7 pr-2 py-1.5 border border-slate-200 rounded-md w-44 focus:outline-none focus:ring-1 focus:ring-emerald-300"
                          data-testid="estimateurs-search"
                        />
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          if (allVisibleSelected) {
                            const ids = new Set(visibleEstimateurs.map((e) => e.id));
                            setFormData({
                              ...formData,
                              estimateurs: (formData.estimateurs || []).filter((id) => !ids.has(id)),
                            });
                          } else {
                            const next = new Set(formData.estimateurs || []);
                            visibleEstimateurs.forEach((e) => next.add(e.id));
                            setFormData({ ...formData, estimateurs: [...next] });
                          }
                        }}
                        className="text-[11px] font-semibold px-2.5 py-1.5 rounded-md border border-emerald-200 bg-white text-emerald-700 hover:bg-emerald-50"
                        data-testid="estimateurs-toggle-all"
                      >
                        {allVisibleSelected ? 'Tout désélectionner' : 'Tout sélectionner'}
                      </button>
                    </div>
                  </div>

                  {visibleEstimateurs.length === 0 ? (
                    <p className="text-sm text-slate-500 italic py-4 text-center">
                      {estimateurs.length === 0
                        ? 'Aucun estimateur disponible'
                        : 'Aucun estimateur ne correspond à votre recherche'}
                    </p>
                  ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-72 overflow-y-auto pr-1">
                      {visibleEstimateurs.map((e) => {
                        const checked = selectedSet.has(e.id);
                        const isTest = /test/i.test(e.name || e.nom || '');
                        const toggle = () => {
                          if (checked) {
                            setFormData({
                              ...formData,
                              estimateurs: (formData.estimateurs || []).filter((id) => id !== e.id),
                            });
                          } else {
                            setFormData({
                              ...formData,
                              estimateurs: [...(formData.estimateurs || []), e.id],
                            });
                          }
                        };
                        return (
                          <button
                            type="button"
                            key={e.id}
                            onClick={toggle}
                            className={`text-left flex items-start gap-2.5 px-3 py-2 rounded-md border transition-all ${
                              checked
                                ? 'bg-emerald-50 border-emerald-300 ring-1 ring-emerald-200'
                                : 'bg-white border-slate-200 hover:border-emerald-200 hover:bg-slate-50'
                            } ${isTest ? 'opacity-60' : ''}`}
                            data-testid={`estimateur-card-${e.id}`}
                          >
                            <Checkbox
                              checked={checked}
                              onCheckedChange={toggle}
                              onClick={(ev) => ev.stopPropagation()}
                              className="mt-0.5 flex-shrink-0"
                            />
                            <div className="min-w-0 flex-1">
                              <div className={`text-sm font-medium leading-snug break-words ${checked ? 'text-emerald-900' : 'text-slate-800'}`}>
                                {e.name || e.nom}
                                {isTest && (
                                  <span className="ml-1.5 text-[9px] font-bold text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded">
                                    TEST
                                  </span>
                                )}
                              </div>
                              {(e.code || e.description) && (
                                <div className="text-[11px] text-slate-500 leading-snug mt-0.5">
                                  {e.code && <span className="font-mono mr-1.5">{e.code}</span>}
                                  {e.description && (
                                    <span className="line-clamp-2">{e.description}</span>
                                  )}
                                </div>
                              )}
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })()}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => { setCreateDialogOpen(false); setEditDialogOpen(false); resetForm(); }}>
              Annuler
            </Button>
            <Button onClick={editDialogOpen ? handleUpdate : handleCreate} className="bg-indigo-600 hover:bg-indigo-700">
              <Save className="h-4 w-4 mr-2" />
              {editDialogOpen ? 'Enregistrer' : 'Créer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
